import React from "react";
import { Helmet } from "react-helmet-async";
import { SEOLanguage, getAlternateUrls } from "@/data/seoTranslations";

interface SEOProps {
  title?: string;
  description?: string;
  keywords?: string;
  canonical?: string;
  ogImage?: string;
  ogType?: string;
  twitterCard?: string;
  structuredData?: object | object[];
  noindex?: boolean;
  nofollow?: boolean;
  language?: SEOLanguage;
  /** Set false on error pages to avoid hreflang pointing at non-existent alternates */
  includeHrefLang?: boolean;
}

const SEO: React.FC<SEOProps> = ({
  title = "Beatenberg Resort & Spa - Swiss Alpine Majesty at its Best",
  description = "Switzerland's purest wellness sanctuary. 15-acre luxury resort in Beatenberg with panoramic Alpine views, NutraSwiss spa programs, and Swiss hospitality.",
  keywords = "Beatenberg, Swiss Alps, luxury resort, wellness spa, Alpine views, Lake Thun, NutraSwiss, Switzerland hotel, mountain resort, spa retreat, Alpine wellness, Swiss hospitality",
  canonical,
  ogImage = "/og-image.jpg",
  ogType = "website",
  twitterCard = "summary_large_image",
  structuredData,
  noindex = false,
  nofollow = false,
  language = "en",
  includeHrefLang = true,
}) => {
  const siteUrl = "https://beatenberg-resortspa.ch";
  // Use path-based language routing for non-English languages
  const fullCanonical = canonical
    ? `${siteUrl}${language !== "en" ? `/${language}` : ""}${canonical}`
    : `${siteUrl}${language !== "en" ? `/${language}/` : "/"}`;
  const fullOgImage = ogImage.startsWith("http")
    ? ogImage
    : `${siteUrl}${ogImage}`;

  const robotsContent = [
    noindex ? "noindex" : "index",
    nofollow ? "nofollow" : "follow",
  ].join(", ");

  // Get alternate URLs for hreflang
  const alternateUrls = canonical
    ? getAlternateUrls(canonical, siteUrl)
    : getAlternateUrls("/", siteUrl);

  // Map language codes to HTML lang attribute format
  const htmlLangMap: Record<SEOLanguage, string> = {
    en: "en",
    de: "de",
    fr: "fr",
    "zh-CN": "zh-CN",
    ko: "ko",
  };

  // Map language codes to Open Graph locale format
  const ogLocaleMap: Record<SEOLanguage, string> = {
    en: "en_US",
    de: "de_DE",
    fr: "fr_FR",
    "zh-CN": "zh_CN",
    ko: "ko_KR",
  };

  return (
    <Helmet data-oid="478agku">
      {/* HTML Language Attribute */}
      <html lang={htmlLangMap[language]} data-oid="ru2o0z." />

      {/* Prevent Browser Auto-Translation */}
      <meta name="google" content="notranslate" data-oid="_xpc159" />
      <meta
        httpEquiv="content-language"
        content={htmlLangMap[language]}
        data-oid="b2xf_5_"
      />

      {/* Basic Meta Tags */}
      <title data-oid="sa7yxl_">{title}</title>
      <meta name="description" content={description} data-oid="uc0f0xr" />
      <meta name="keywords" content={keywords} data-oid="u4j:wju" />
      <meta
        name="author"
        content="Beatenberg Resort & Spa"
        data-oid="mzl6e2c"
      />

      <meta name="robots" content={robotsContent} data-oid="6aq3346" />
      <link rel="canonical" href={fullCanonical} data-oid=".t9e7ge" />

      {/* Hreflang Tags for International SEO */}
      {includeHrefLang &&
        Object.entries(alternateUrls).map(([lang, url]) => (
          <link
            key={lang}
            rel="alternate"
            hrefLang={lang}
            href={url}
            data-oid=":nga_v."
          />
        ))}

      {/* Open Graph Meta Tags */}
      <meta property="og:title" content={title} data-oid="2rp8nue" />
      <meta
        property="og:description"
        content={description}
        data-oid="2i5xxh6"
      />

      <meta property="og:type" content={ogType} data-oid="crxcrcu" />
      <meta property="og:url" content={fullCanonical} data-oid="bhcwy9z" />
      <meta
        property="og:site_name"
        content="Beatenberg Resort & Spa"
        data-oid="a_:e:iq"
      />

      <meta property="og:image" content={fullOgImage} data-oid="mk0kgdi" />
      <meta property="og:image:width" content="1200" data-oid="5g-_i:l" />
      <meta property="og:image:height" content="630" data-oid=".f-:kz4" />
      <meta property="og:image:alt" content={title} data-oid=".ykkap8" />
      <meta
        property="og:locale"
        content={ogLocaleMap[language]}
        data-oid="7jhzqzn"
      />

      {/* Alternate locales for Open Graph */}
      {includeHrefLang &&
        Object.keys(alternateUrls)
          .filter((lang) => lang !== "x-default" && lang !== language)
          .map((lang) => (
            <meta
              key={`og-alt-${lang}`}
              property="og:locale:alternate"
              content={ogLocaleMap[lang as SEOLanguage]}
              data-oid="hgfwkrh"
            />
          ))}

      {/* Twitter Card Meta Tags */}
      <meta name="twitter:card" content={twitterCard} data-oid="3kn2adx" />
      <meta name="twitter:title" content={title} data-oid="0du0b-1" />
      <meta
        name="twitter:description"
        content={description}
        data-oid="vjlnz4h"
      />

      <meta name="twitter:image" content={fullOgImage} data-oid="m2h.8ki" />
      <meta name="twitter:image:alt" content={title} data-oid="uatrhou" />

      {/* Additional SEO Meta Tags */}
      <meta name="theme-color" content="#8B7355" data-oid="qm6b9qw" />
      <meta
        name="msapplication-TileColor"
        content="#8B7355"
        data-oid="sqv66rw"
      />

      <meta name="mobile-web-app-capable" content="yes" data-oid="8yv77iw" />
      <meta
        name="apple-mobile-web-app-capable"
        content="yes"
        data-oid="gumgnt."
      />

      <meta
        name="apple-mobile-web-app-status-bar-style"
        content="default"
        data-oid="l0j7pmi"
      />

      <meta
        name="apple-mobile-web-app-title"
        content="Beatenberg Resort & Spa"
        data-oid="c50zxfc"
      />

      {/* Geographic Meta Tags */}
      <meta name="geo.region" content="CH-BE" data-oid="lopshv0" />
      <meta name="geo.placename" content="Beatenberg" data-oid="j38cs25" />
      <meta name="geo.position" content="46.6969;7.7944" data-oid="dne3hv:" />
      <meta name="ICBM" content="46.6969, 7.7944" data-oid="9.1_0xj" />

      {/* Business/Local SEO */}
      <meta
        name="business:contact_data:street_address"
        content="Spirenwaldstrasse 351"
        data-oid="l_8g5xp"
      />

      <meta
        name="business:contact_data:locality"
        content="Beatenberg"
        data-oid="cnv7.rd"
      />

      <meta
        name="business:contact_data:region"
        content="Bern"
        data-oid="mevdy12"
      />

      <meta
        name="business:contact_data:postal_code"
        content="3803"
        data-oid="ou8uw09"
      />

      <meta
        name="business:contact_data:country_name"
        content="Switzerland"
        data-oid="1md3ngb"
      />

      {/* Structured Data */}
      {structuredData &&
        (Array.isArray(structuredData) ? (
          // Render separate script tags for each schema
          structuredData.map((schema, index) => (
            <script key={index} type="application/ld+json" data-oid="q-w:kiw">
              {JSON.stringify(schema)}
            </script>
          ))
        ) : (
          // Render single schema
          <script type="application/ld+json" data-oid="8.p9j1q">
            {JSON.stringify(structuredData)}
          </script>
        ))}
    </Helmet>
  );
};

export default SEO;
